<template>
  <article
    class="dist-product-card panel-card"
    :class="{ 'dist-product-card--active': subtotal > 0 }"
  >
    <div
      class="dist-product-card__media"
      role="button"
      tabindex="0"
      @click="emit('open-info', product)"
      @keydown.enter="emit('open-info', product)"
    >
      <ProductImage :product="product" :alt="product.name" class="dist-product-card__image" />
      <span v-if="productOfferPct > 0" class="dist-product-card__offer">
        {{ hasMixedOffers ? 'Hasta ' : '' }}−{{ productOfferPct }}% OFF
      </span>
      <span v-if="totalQty > 0" class="dist-product-card__badge">{{ totalQty }}</span>
    </div>

    <div class="dist-product-card__body">
      <div class="dist-product-card__head">
        <h3 class="dist-product-card__title">{{ product.name }}</h3>
        <div class="dist-product-card__meta">
          <p v-if="product.brand_name || product.brand" class="dist-product-card__brand text-muted">
            {{ product.brand_name || product.brand }}
          </p>
          <span class="type-badge fem" v-if="product.type === 'fem'">Fotoperiódica</span>
          <span class="type-badge auto" v-else-if="product.type === 'auto'">Autofloreciente</span>
        </div>
      </div>

      <ul class="dist-product-card__blisters">
        <li
          v-for="blister in sortedBlisters"
          :key="blister.id"
          class="blister-row"
          :class="{ 'blister-row--disabled': !blister.stock || blister.prices_visible === false }"
        >
          <div class="blister-row__info">
            <span class="blister-row__seeds">
              {{ blister.seed_count }} {{ ['auto', 'fem'].includes(product.type) ? 'semillas' : 'unidades' }}
            </span>
            <span v-if="blister.prices_visible !== false" class="blister-row__price">
              <span
                v-if="blister.offer_discount_percent > 0 && blister.list_price"
                class="blister-row__price-list"
              >{{ formatPrice(blister.list_price) }}</span>
              <span :class="{ 'blister-row__price-final--offer': blister.offer_discount_percent > 0 }">
                {{ formatPrice(blister.final_price) }}
              </span>
            </span>
            <span v-else class="blister-row__price text-muted">—</span>
            <span class="blister-row__stock" :class="{ 'text-danger': blister.stock === 0 }">
              {{ blister.stock === 0 ? 'Sin stock' : 'En stock' }}
            </span>
          </div>
          <div class="blister-row__actions">
            <div class="qty-wrapper">
              <button
                type="button"
                class="qty-btn"
                :disabled="qtyFor(blister) <= 0"
                @click="emit('set-qty', product, blister, qtyFor(blister) - 1)"
              >
                −
              </button>
              <input
                type="number"
                class="qty-input"
                :value="qtyFor(blister)"
                min="0"
                :max="blister.stock"
                :disabled="blister.stock === 0 || blister.prices_visible === false"
                @change="onInput(blister, $event)"
              />
              <button
                type="button"
                class="qty-btn"
                :disabled="blister.stock === 0 || blister.prices_visible === false"
                @click="onIncrement(blister)"
              >
                +
              </button>
            </div>
            <transition name="stock-warn">
              <div v-if="stockWarnings[lineKey(blister)]" class="stock-warning">
                Sin stock disponible
              </div>
            </transition>
          </div>
        </li>
      </ul>

      <div class="dist-product-card__foot">
        <div v-if="subtotal > 0" class="dist-product-card__subtotal">
          <span class="text-muted small">Subtotal</span>
          <strong>{{ formatPrice(subtotal) }}</strong>
        </div>
        <button
          type="button"
          class="btn btn-outline-light dist-product-card__info-btn"
          title="Ver información del producto"
          @click.stop="emit('open-info', product)"
        >
          <i class="fas fa-info-circle me-2"></i>Más info
        </button>
      </div>
    </div>
  </article>
</template>

<script setup>
import ProductImage from '@/components/ProductImage.vue'
import { computed, reactive } from 'vue'
import { distributorLineKey } from '@/utils/distributorProduct'

const props = defineProps({
  product: { type: Object, required: true },
  quantities: { type: Object, required: true },
  subtotal: { type: Number, default: 0 },
})

const emit = defineEmits(['set-qty', 'open-info'])

const lineKey = (blister) => distributorLineKey(props.product, blister)

const qtyFor = (blister) => props.quantities[lineKey(blister)] || 0

const sortedBlisters = computed(() =>
  [...(props.product.blisters || [])].sort((a, b) => Number(a.seed_count) - Number(b.seed_count))
)

const totalQty = computed(() =>
  sortedBlisters.value.reduce((sum, b) => sum + qtyFor(b), 0)
)

// Mayor % de oferta activa entre las presentaciones del producto.
const productOfferPct = computed(() =>
  sortedBlisters.value.reduce(
    (max, b) => Math.max(max, Number(b.offer_discount_percent) || 0),
    0
  )
)

const hasMixedOffers = computed(() => sortedBlisters.value.some(
  b => (Number(b.offer_discount_percent) || 0) !== productOfferPct.value
))

const formatPrice = (v) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(v || 0)


const onInput = (blister, event) => {
  const qty = parseInt(event.target.value, 10) || 0
  emit('set-qty', props.product, blister, qty)
}

// Mensaje inline "sin stock" al intentar agregar más de lo disponible
const stockWarnings = reactive({})
const stockWarningTimers = {}

const showStockWarning = (blister) => {
  const k = lineKey(blister)
  stockWarnings[k] = Date.now()
  clearTimeout(stockWarningTimers[k])
  stockWarningTimers[k] = setTimeout(() => {
    delete stockWarnings[k]
    delete stockWarningTimers[k]
  }, 2000)
}

const onIncrement = (blister) => {
  if (qtyFor(blister) >= blister.stock) {
    showStockWarning(blister)
    return
  }
  emit('set-qty', props.product, blister, qtyFor(blister) + 1)
}
</script>

<style scoped>
.dist-product-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.1));
  transition: border-color 0.15s, box-shadow 0.15s;
}

.dist-product-card--active {
  border-color: rgba(255, 236, 0, 0.35);
  box-shadow: 0 0 0 1px rgba(255, 236, 0, 0.12);
}

.dist-product-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.dist-product-card__media:focus-visible {
  outline: 2px solid var(--primary-color, #ffec00);
  outline-offset: 2px;
}

.dist-product-card__head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.dist-product-card__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
}

.dist-product-card__brand {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
}

.dist-product-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  margin-top: auto;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.dist-product-card__foot .dist-product-card__subtotal {
  padding-top: 0;
  margin-top: 0;
  border-top: none;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  flex: 1 1 auto;
  min-width: 0;
}

.dist-product-card__info-btn {
  flex: 0 0 auto;
  margin-left: auto;
  min-height: 2.35rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
}

.dist-product-card__image {
  width: 100%;
  height: 100%;
  max-height: 250px;
  object-fit: contain;
  padding: 0.75rem;
}

.dist-product-card__image--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: rgba(255, 255, 255, 0.25);
}

.dist-product-card__badge {
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  min-width: 1.6rem;
  height: 1.6rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #ffec00;
  color: var(--black-color, #111);
  font-size: 0.78rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dist-product-card__body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
}

.dist-product-card__offer {
  position: absolute;
  top: 0.65rem;
  left: 0.65rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #FF7731;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.blister-row__price-list {
  display: inline-block;
  margin-right: 0.35rem;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: line-through;
  font-size: 0.72rem;
}

.blister-row__price-final--offer {
  color: var(--primary-color, #ffec00);
  font-weight: 700;
}

.dist-product-card__title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.35;
  min-width: 0;
}

.type-badge {
  display: inline-block;
  font-size: 0.68rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.type-badge.reg {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.65);
}

.type-badge.fem {
  background: rgba(236, 106, 31, 0.15);
  color: #f0a070;
}

.type-badge.auto {
  background: rgba(255, 236, 0, 0.12);
  color: var(--primary-color, #ffec00);
}

.dist-product-card__blisters {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.blister-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.65rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.blister-row--disabled {
  opacity: 0.55;
}

.blister-row__info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.blister-row__seeds {
  font-size: 0.82rem;
  font-weight: 600;
}

.blister-row__price {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.75);
}

.blister-row__stock {
  font-size: 0.68rem;
  color: var(--panel-text-muted, rgba(255, 255, 255, 0.45));
}

.dist-product-card__subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.dist-product-card__subtotal strong {
  color: var(--primary-color, #ffec00);
}

.qty-wrapper {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.blister-row__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.stock-warning {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ff5a5a;
  line-height: 1.2;
  text-align: right;
}

.stock-warn-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.stock-warn-leave-active { transition: opacity 0.4s ease, transform 0.4s ease; }
.stock-warn-enter-from { opacity: 0; transform: translateY(4px); }
.stock-warn-leave-to   { opacity: 0; transform: translateY(-6px); }

.qty-btn {
  width: 28px;
  height: 28px;
  background: var(--panel-surface, rgba(0, 0, 0, 0.3));
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.15));
  color: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.qty-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.4);
}

.qty-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.qty-input {
  width: 44px;
  text-align: center;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.15));
  color: var(--white-color, #fff);
  border-radius: 6px;
  font-size: 0.85rem;
  padding: 4px;
}

.qty-input::-webkit-inner-spin-button {
  display: none;
}

@media (max-width: 360px) {
  .dist-product-card__foot {
    flex-direction: column;
    align-items: stretch;
  }

  .dist-product-card__foot .dist-product-card__subtotal {
    margin-right: 0;
    flex-direction: row;
    justify-content: space-between;
  }

  .dist-product-card__info-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
